import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { SmartImage } from "@/components/movie/SmartImage";
import { GlassButton } from "@/components/glass/Glass";
import { WatchlistButton } from "@/components/movie/WatchlistButton";
import { Dot, QualityBadge, Rating } from "@/components/movie/MovieMeta";
import { IconPlay } from "@/components/icons/Icons";
import { formatDuration } from "@/lib/format";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { LOW_POWER } from "@/lib/perf";

const ROTATE_MS = 9000;

export function HeroMovie({ movies }) {
  const list = useMemo(() => (movies || []).slice(0, 5), [movies]);
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const reduce = useReducedMotion();
  const navigate = useNavigate();
  const { scrollY } = useScroll();
  const still = reduce || LOW_POWER; // no scroll-linked parallax on phones
  const y = useTransform(scrollY, [0, 700], [0, still ? 0 : 90]);
  const scale = useTransform(scrollY, [0, 700], [1.04, still ? 1.04 : 1.1]);
  const m = list[i] || list[0];

  useEffect(() => {
    if (list.length < 2 || paused || reduce) return;
    const t = setTimeout(() => setI((v) => (v + 1) % list.length), ROTATE_MS);
    return () => clearTimeout(t);
  }, [i, list.length, paused, reduce]);

  // Warm up only the next hero image shortly before rotation.
  useEffect(() => {
    if (list.length < 2) return;
    const next = list[(i + 1) % list.length];
    const src = next?.backdrop || next?.poster;
    const t = setTimeout(() => {
      if (src) {
        const img = new Image();
        img.decoding = "async";
        img.src = src;
      }
    }, ROTATE_MS - 2500);
    return () => clearTimeout(t);
  }, [i, list]);

  if (!m) return null;
  const play = () => {
    if (!m.hasVideo) return toast("Video hozircha mavjud emas.");
    navigate(`/watch/${m.id}`);
  };

  return (
    <section className="relative h-[78svh] min-h-[520px] max-h-[880px] w-full overflow-hidden" data-testid="home-hero" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} aria-roledescription="karusel">
      <AnimatePresence initial={false}>
        <motion.div key={m.id} className="absolute inset-0" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 1.1, ease: [0.2, 0.9, 0.2, 1] }}>
          <motion.div className={cn("absolute inset-0", !still && "will-change-transform")} style={still ? { scale: 1.04 } : { y, scale }}>
            <SmartImage src={m.backdrop || m.poster} alt="" priority variant="backdrop" className="absolute inset-0" imgClassName={cn(!m.backdrop && "blur-2xl scale-110")} />
          </motion.div>
        </motion.div>
      </AnimatePresence>
      <div className="pk-vignette pointer-events-none absolute inset-0" />
      <div className="pk-fade-left pointer-events-none absolute inset-0 hidden md:block" />
      <div className="pk-fade-bottom pointer-events-none absolute inset-x-0 bottom-0 h-[70%]" />

      <div className="pk-container absolute inset-x-0 bottom-[9%] sm:bottom-[12%]">
        <AnimatePresence mode="wait">
          <motion.div
            key={m.id}
            className="max-w-[640px]"
            initial={reduce ? { opacity: 0 } : LOW_POWER ? { opacity: 0, y: 12 } : { opacity: 0, filter: "blur(14px)", y: 10 }}
            animate={LOW_POWER || reduce ? { opacity: 1, y: 0 } : { opacity: 1, filter: "blur(0px)", y: 0 }}
            exit={reduce ? { opacity: 0 } : LOW_POWER ? { opacity: 0, y: -6 } : { opacity: 0, filter: "blur(10px)", y: -6 }}
            transition={{ duration: 0.6, ease: [0.2, 0.9, 0.2, 1] }}
          >
            {m.featured && <span className="pk-glass mb-4 inline-flex h-7 items-center rounded-full px-3 text-[12px] font-semibold text-white/90">Tavsiya etamiz</span>}
            <h1 className="text-[40px] font-black leading-[1.02] tracking-[-0.03em] text-white drop-shadow-[0_4px_30px_rgba(0,0,0,.5)] sm:text-[54px] lg:text-[68px]" data-testid="home-hero-title">
              {m.title}
            </h1>
            <div className="mt-4 flex flex-wrap items-center gap-x-2.5 gap-y-2 text-[14px] font-medium text-white/75">
              <Rating value={m.rating} className="text-[14px]" />
              {m.rating !== null && <Dot />}
              {m.year && <span>{m.year}</span>}
              {m.duration && (
                <>
                  <Dot />
                  <span>{formatDuration(m.duration)}</span>
                </>
              )}
              {m.genres?.[0] && (
                <>
                  <Dot />
                  <span>{m.genres.slice(0, 2).map((g) => g.name).join(", ")}</span>
                </>
              )}
              {m.quality?.map((q) => (
                <QualityBadge key={q}>{q}</QualityBadge>
              ))}
            </div>
            {m.description && <p className="mt-4 line-clamp-2 max-w-[56ch] text-[15px] leading-relaxed text-white/70 sm:line-clamp-3 sm:text-[16px]">{m.description}</p>}
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <GlassButton variant="primary" size="lg" onClick={play} data-testid="hero-play-button">
                <IconPlay size={18} /> Tomosha qilish
              </GlassButton>
              <WatchlistButton movieId={m.id} testId="hero-watchlist-button" />
              <GlassButton size="lg" variant="ghost" onClick={() => navigate(`/movie/${m.id}`)} data-testid="hero-details-button" className="hidden sm:inline-flex">
                Batafsil
              </GlassButton>
            </div>
          </motion.div>
        </AnimatePresence>

        {list.length > 1 && (
          <div className="mt-8 flex items-center gap-2" role="tablist" aria-label="Tanlangan filmlar">
            {list.map((x, idx) => (
              <button
                key={x.id}
                role="tab"
                aria-selected={idx === i}
                aria-label={x.title}
                onClick={() => setI(idx)}
                data-testid={`hero-dot-${idx}`}
                className={cn("relative h-1.5 overflow-hidden rounded-full transition-[width,background-color] duration-500", idx === i ? "w-10 bg-white/25" : "w-3 bg-white/30 hover:bg-white/60")}
              >
                {idx === i && (
                  <motion.span
                    key={`${x.id}-${paused}`}
                    className="absolute inset-0 origin-left rounded-full bg-white"
                    initial={{ scaleX: paused || reduce ? 1 : 0 }}
                    animate={{ scaleX: 1 }}
                    transition={{ duration: paused || reduce ? 0 : ROTATE_MS / 1000, ease: "linear" }}
                  />
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
